// Iter 62ad — Clients hub (founders only)
// Vue Kanban 4 colonnes (Prospect / Onboarding / En prod / Livré) + panneau latéral
// avec les notes liées au client. Design gold/navy premium.
// Iter 62cq — Onglet Analytics ajouté (Vues + Argent par client, 6 mois d'historique).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import { Plus, X, Trash2, FileText, ChevronRight, Users, LayoutGrid, BarChart3 } from '../components/icons';
import ClientsAnalyticsView from './ClientsAnalyticsView';

const TABS = [
  { id: 'kanban',    label: 'Kanban',    icon: LayoutGrid },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
];

const STATUSES = [
  { id: 'prospect',   label: 'Prospect',    accent: '#8A9DB8', glow: 'rgba(138,157,184,0.25)' },
  { id: 'onboarding', label: 'Onboarding',  accent: '#5BBEFC', glow: 'rgba(91,190,252,0.25)' },
  { id: 'prod',       label: 'En prod',     accent: '#F5C56A', glow: 'rgba(245,197,106,0.25)' },
  { id: 'livre',      label: 'Livré',       accent: '#34D399', glow: 'rgba(52,211,153,0.25)' },
];

const CARD_COLORS = ['#5BBEFC', '#F5C56A', '#A78BFA', '#34D399', '#F97066', '#FBBF24'];

export default function ClientsPage() {
  const { isFounder } = useAuth();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null); // {status} when adding a new card
  const [newName, setNewName] = useState('');
  const [openClient, setOpenClient] = useState(null); // full client obj → opens drawer
  const [dragId, setDragId] = useState(null);
  // Iter 62cq — tab switcher (kanban / analytics)
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem('asa_clients_tab') || 'kanban'; } catch (_e) { return 'kanban'; }
  });

  useEffect(() => {
    try { localStorage.setItem('asa_clients_tab', tab); } catch (_e) { /* noop */ }
  }, [tab]);

  useEffect(() => {
    if (!isFounder) {
      nav('/');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFounder]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/clients');
      setClients(r.data || []);
    } catch (e) {
      toast.error('Impossible de charger les clients');
    } finally {
      setLoading(false);
    }
  };

  const grouped = useMemo(() => {
    const g = { prospect: [], onboarding: [], prod: [], livre: [] };
    (clients || []).forEach((c) => {
      const s = STATUSES.find((x) => x.id === c.status) ? c.status : 'prospect';
      g[s].push(c);
    });
    // sort within column by order then name
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || '')));
    return g;
  }, [clients]);

  const addClient = async (status) => {
    const name = (newName || '').trim();
    if (!name) return;
    const color = CARD_COLORS[Math.floor(Math.random() * CARD_COLORS.length)];
    try {
      const r = await api.post('/clients', { name, status, color });
      setClients((prev) => [...prev, r.data]);
      setNewName('');
      setCreating(null);
      toast.success('Client créé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    }
  };

  const moveClient = async (cid, newStatus) => {
    const c = clients.find((x) => x.id === cid);
    if (!c || c.status === newStatus) return;
    // optimistic
    setClients((prev) => prev.map((x) => (x.id === cid ? { ...x, status: newStatus } : x)));
    try {
      await api.patch(`/clients/${cid}`, { status: newStatus });
    } catch (e) {
      setClients((prev) => prev.map((x) => (x.id === cid ? c : x)));
      toast.error('Erreur déplacement');
    }
  };

  const deleteClient = async (cid) => {
    if (!window.confirm('Supprimer ce client ?\nLes notes rattachées seront conservées (juste détachées).')) return;
    try {
      await api.delete(`/clients/${cid}`);
      setClients((prev) => prev.filter((x) => x.id !== cid));
      setOpenClient(null);
      toast.success('Client supprimé');
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  if (!isFounder) return null;

  return (
    <div className="page-fade">
      <PageHeader
        title="Clients"
        subtitle={tab === 'kanban'
          ? 'Suivi des clients par étape — glisser une carte pour changer son statut'
          : 'Vues cumulées + revenus par client sur les 6 derniers mois'}
      />

      {/* Iter 62cq — Tab switcher */}
      <div
        data-testid="clients-tabs"
        style={{
          display: 'flex', gap: 8,
          padding: '0 22px 18px',
        }}
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`clients-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '9px 16px', borderRadius: 999,
                border: `1px solid ${active ? '#F5C56A' : 'var(--bd)'}`,
                background: active
                  ? 'linear-gradient(140deg, rgba(245,197,106,0.20), rgba(245,197,106,0.06))'
                  : 'var(--sf)',
                color: active ? '#F5C56A' : 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
                letterSpacing: '-0.005em',
                cursor: 'pointer',
                boxShadow: active ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 14px rgba(245,197,106,0.20)' : 'none',
                transition: 'all 0.15s var(--ease-out)',
              }}
            >
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'analytics' ? (
        <ClientsAnalyticsView />
      ) : (
        <div style={{ padding: '0 22px 100px' }}>
        {loading ? (
          <div className="shimmer" style={{ height: 300, borderRadius: 16 }} />
        ) : (
          <div
            data-testid="clients-kanban"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(240px, 1fr))',
              gap: 14,
              alignItems: 'flex-start',
            }}
          >
            {STATUSES.map((col) => (
              <Column
                key={col.id}
                col={col}
                items={grouped[col.id] || []}
                creating={creating === col.id}
                newName={newName}
                setNewName={setNewName}
                onStartCreate={() => { setCreating(col.id); setNewName(''); }}
                onCancelCreate={() => { setCreating(null); setNewName(''); }}
                onConfirmCreate={() => addClient(col.id)}
                dragId={dragId}
                setDragId={setDragId}
                onDropCard={(cid) => moveClient(cid, col.id)}
                onOpenCard={(c) => nav(`/clients/${c.id}`)}
              />
            ))}
          </div>
        )}
        </div>
      )}

      {openClient && (
        <ClientDrawer
          client={openClient}
          onClose={() => setOpenClient(null)}
          onDelete={() => deleteClient(openClient.id)}
          onNotesCountChange={(newCount) => {
            setClients((prev) => prev.map((x) => (x.id === openClient.id ? { ...x, note_count: newCount } : x)));
            setOpenClient((prev) => ({ ...prev, note_count: newCount }));
          }}
          onNameChange={(newName) => {
            setClients((prev) => prev.map((x) => (x.id === openClient.id ? { ...x, name: newName } : x)));
            setOpenClient((prev) => ({ ...prev, name: newName }));
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Kanban column
// -----------------------------------------------------------------------------
function Column({
  col, items, creating, newName, setNewName,
  onStartCreate, onCancelCreate, onConfirmCreate,
  dragId, setDragId, onDropCard, onOpenCard,
}) {
  const [dragOver, setDragOver] = useState(false);

  return (
    <div
      data-testid={`kanban-col-${col.id}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const cid = e.dataTransfer.getData('text/plain') || dragId;
        if (cid) onDropCard(cid);
        setDragId(null);
      }}
      style={{
        borderRadius: 16,
        background:
          `linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01)), var(--sf)`,
        border: `1px solid ${dragOver ? col.accent : 'var(--bd)'}`,
        boxShadow: dragOver
          ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 26px ${col.glow}`
          : 'inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 18px rgba(0,0,0,0.25)',
        padding: 12,
        minHeight: 300,
        transition: 'all 0.15s var(--ease-out)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 4px 12px' }}>
        <span
          aria-hidden
          style={{
            width: 6, height: 6, borderRadius: 999,
            background: col.accent,
            boxShadow: `0 0 10px ${col.glow}`,
          }}
        />
        <span
          style={{
            flex: 1,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            color: col.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
          }}
        >{col.label}</span>
        <span
          style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
            color: 'var(--t3)',
          }}
        >{items.length}</span>
      </div>

      {/* Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((c) => (
          <ClientCard
            key={c.id}
            client={c}
            onDragStart={() => setDragId(c.id)}
            onClick={() => onOpenCard(c)}
          />
        ))}
      </div>

      {/* Create row */}
      {creating ? (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            borderRadius: 12,
            border: `1px solid ${col.accent}`,
            background: 'var(--sf2)',
            boxShadow: `0 4px 14px ${col.glow}`,
          }}
        >
          <input
            data-testid={`kanban-new-input-${col.id}`}
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nom du client…"
            onKeyDown={(e) => {
              if (e.key === 'Enter') onConfirmCreate();
              if (e.key === 'Escape') onCancelCreate();
            }}
            style={{
              width: '100%',
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 8,
              padding: '8px 10px',
              color: 'var(--t1)',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
              outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button
              data-testid={`kanban-new-confirm-${col.id}`}
              onClick={onConfirmCreate}
              style={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: 8,
                border: 'none',
                background: col.accent,
                color: '#0B1220',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
                cursor: 'pointer',
              }}
            >Créer</button>
            <button
              onClick={onCancelCreate}
              style={{
                padding: '7px 10px',
                borderRadius: 8,
                border: '1px solid var(--bd)',
                background: 'transparent',
                color: 'var(--t2)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                cursor: 'pointer',
              }}
            >Annuler</button>
          </div>
        </div>
      ) : (
        <button
          data-testid={`kanban-add-${col.id}`}
          onClick={onStartCreate}
          style={{
            marginTop: 8,
            width: '100%',
            padding: '10px 12px',
            borderRadius: 10,
            border: '1px dashed var(--bd)',
            background: 'transparent',
            color: 'var(--t3)',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            transition: 'all 0.15s var(--ease-out)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = col.accent;
            e.currentTarget.style.color = col.accent;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--bd)';
            e.currentTarget.style.color = 'var(--t3)';
          }}
        >
          <Plus size={13} />
          Ajouter un client
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Kanban card
// -----------------------------------------------------------------------------
function ClientCard({ client, onDragStart, onClick }) {
  const color = client.color || '#5BBEFC';
  const initials = (client.name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  return (
    <div
      data-testid={`client-card-${client.id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', client.id);
        onDragStart();
      }}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 12px',
        borderRadius: 12,
        border: '1px solid var(--bd)',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01)), var(--sf2)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 12px rgba(0,0,0,0.28)',
        cursor: 'grab',
        overflow: 'hidden',
        transition: 'transform 0.15s var(--ease-out), border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--bd)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span
        aria-hidden
        style={{
          width: 34, height: 34, borderRadius: 10,
          display: 'grid', placeItems: 'center',
          background:
            `linear-gradient(140deg, ${color} 0%, rgba(255,255,255,0.15) 50%, ${color} 100%)`,
          color: '#0B1220',
          fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
          letterSpacing: '0.02em',
          boxShadow:
            `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -5px 8px rgba(0,0,0,0.25), 0 3px 10px ${color}55`,
          flexShrink: 0,
        }}
      >{initials || '?'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
          color: 'var(--t1)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{client.name}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
          color: 'var(--t3)', marginTop: 2,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <FileText size={10} />
          {client.note_count || 0} note{(client.note_count || 0) > 1 ? 's' : ''}
        </div>
      </div>
      <ChevronRight size={14} color="var(--t4)" />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Side drawer showing all notes linked to a client
// -----------------------------------------------------------------------------
function ClientDrawer({ client, onClose, onDelete, onNotesCountChange, onNameChange }) {
  const nav = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(client.name || '');

  useEffect(() => {
    setName(client.name || '');
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/clients/${client.id}/notes`);
        if (!cancelled) setNotes(r.data || []);
      } catch (e) {
        if (!cancelled) toast.error('Impossible de charger les notes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client.id, client.name]);

  const createNote = async () => {
    const t = (newTitle || '').trim() || 'Sans titre';
    setCreating(true);
    try {
      const r = await api.post('/notes', {
        title: t,
        content: '',
        note_type: 'client',
        client_id: client.id,
        visibility: 'shared',
      });
      const next = [r.data, ...notes];
      setNotes(next);
      onNotesCountChange(next.length);
      setNewTitle('');
      toast.success('Note créée');
      // Deep-link to the note editor for immediate write
      nav(`/notes?note=${r.data.id}`);
    } catch (e) {
      toast.error('Erreur création note');
    } finally {
      setCreating(false);
    }
  };

  const saveName = async () => {
    const trimmed = (name || '').trim();
    if (!trimmed || trimmed === client.name) {
      setEditingName(false);
      setName(client.name || '');
      return;
    }
    try {
      await api.patch(`/clients/${client.id}`, { name: trimmed });
      onNameChange(trimmed);
      setEditingName(false);
      toast.success('Renommé');
    } catch (e) {
      toast.error('Erreur');
      setName(client.name || '');
      setEditingName(false);
    }
  };

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 900,
          background: 'rgba(3,7,18,0.55)',
          backdropFilter: 'blur(6px)',
        }}
      />
      {/* panel */}
      <aside
        data-testid="client-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 901,
          width: 'min(460px, 100vw)',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--bd)',
          boxShadow: '-24px 0 60px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column',
          animation: 'slideInRight 0.22s var(--ease-out)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 14px',
          borderBottom: '1px solid var(--bd)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span
            aria-hidden
            style={{
              width: 44, height: 44, borderRadius: 12,
              display: 'grid', placeItems: 'center',
              background:
                `linear-gradient(140deg, ${client.color} 0%, rgba(255,255,255,0.15) 50%, ${client.color} 100%)`,
              color: '#0B1220',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
              boxShadow:
                `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 14px ${client.color}55`,
              flexShrink: 0,
            }}
          >
            {(client.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('')}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingName ? (
              <input
                data-testid="client-name-input"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') { setEditingName(false); setName(client.name || ''); }
                }}
                style={{
                  width: '100%',
                  background: 'var(--sf2)',
                  border: '1px solid var(--bd)',
                  borderRadius: 8,
                  padding: '6px 10px',
                  color: 'var(--t1)',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 18,
                  outline: 'none',
                }}
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                style={{
                  background: 'none', border: 'none',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 20,
                  color: 'var(--t1)', letterSpacing: '-0.01em',
                  cursor: 'text', padding: 0,
                }}
              >{client.name}</button>
            )}
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
              color: 'var(--t3)', marginTop: 2,
              textTransform: 'uppercase', letterSpacing: '0.1em',
            }}>{STATUSES.find((s) => s.id === client.status)?.label || 'Prospect'} · {notes.length} note{notes.length > 1 ? 's' : ''}</div>
          </div>
          <button
            data-testid="client-drawer-close"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              display: 'grid', placeItems: 'center',
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              color: 'var(--t2)',
              cursor: 'pointer',
            }}
            aria-label="Fermer"
          >
            <X size={15} />
          </button>
        </div>

        {/* New note bar */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bd)', display: 'flex', gap: 8 }}>
          <input
            data-testid="client-new-note-input"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Nouvelle note pour ce client…"
            onKeyDown={(e) => { if (e.key === 'Enter') createNote(); }}
            style={{
              flex: 1,
              background: 'var(--sf2)',
              border: '1px solid var(--bd)',
              borderRadius: 10,
              padding: '9px 12px',
              color: 'var(--t1)',
              fontFamily: 'Sora', fontWeight: 600, fontSize: 13,
              outline: 'none',
            }}
          />
          <button
            data-testid="client-new-note-btn"
            onClick={createNote}
            disabled={creating}
            style={{
              padding: '9px 14px',
              borderRadius: 10,
              border: 'none',
              background: 'linear-gradient(140deg, #F5C56A 0%, #E5B04D 100%)',
              color: '#0B1220',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
              cursor: creating ? 'default' : 'pointer',
              opacity: creating ? 0.6 : 1,
              boxShadow: '0 4px 12px rgba(245,197,106,0.35)',
            }}
          >
            <Plus size={12} style={{ verticalAlign: -1, marginRight: 3 }} />
            Note
          </button>
        </div>

        {/* Notes list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
          {loading ? (
            <div className="shimmer" style={{ height: 60, borderRadius: 10, margin: 6 }} />
          ) : notes.length === 0 ? (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
            }}>
              <Users size={30} color="var(--t4)" strokeWidth={1.4} />
              <div style={{ marginTop: 12, fontWeight: 600 }}>Aucune note pour ce client</div>
              <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--t4)' }}>
                Utilise le champ ci-dessus pour en créer une
              </div>
            </div>
          ) : (
            notes.map((n) => (
              <button
                key={n.id}
                data-testid={`client-note-${n.id}`}
                onClick={() => { onClose(); nav(`/notes?note=${n.id}`); }}
                style={{
                  width: '100%',
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '12px 12px',
                  borderRadius: 10,
                  border: '1px solid var(--bd)',
                  background: 'var(--sf)',
                  color: 'var(--t1)',
                  textAlign: 'left',
                  marginBottom: 6,
                  cursor: 'pointer',
                  transition: 'all 0.15s var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--sf2)';
                  e.currentTarget.style.borderColor = client.color;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--sf)';
                  e.currentTarget.style.borderColor = 'var(--bd)';
                }}
              >
                <FileText size={14} color={client.color} strokeWidth={1.8} style={{ marginTop: 1, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
                    color: 'var(--t1)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{n.title || 'Sans titre'}</div>
                  <div style={{
                    fontFamily: 'Sora', fontWeight: 500, fontSize: 11,
                    color: 'var(--t3)', marginTop: 3,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {stripHtml(n.content || '')}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid var(--bd)',
          display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
        }}>
          <button
            data-testid="client-portal-link-btn"
            onClick={async () => {
              try {
                const { data } = await api.post(`/clients/${client.id}/portal-link`);
                const url = data.url.startsWith('http') ? data.url : `${window.location.origin}${data.url}`;
                await navigator.clipboard.writeText(url);
                toast.success('Lien du portail copié ✓');
              } catch (e) {
                toast.error('Erreur génération lien');
              }
            }}
            style={{
              padding: '7px 12px', borderRadius: 8,
              border: '1px solid rgba(91,190,252,0.45)',
              background: 'linear-gradient(135deg, rgba(91,190,252,0.15), rgba(62,123,250,0.10))',
              color: '#5BBEFC',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            🔗 Copier lien portail
          </button>
          <button
            data-testid="client-delete-btn"
            onClick={onDelete}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: '1px solid rgba(255,107,107,0.35)',
              background: 'transparent',
              color: '#FF6B6B',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <Trash2 size={12} /> Supprimer client
          </button>
        </div>
      </aside>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function stripHtml(s) {
  if (!s) return '';
  return s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}
